/**
 * Validate a single proposal op against the component registry. Used before
 * applyOps writes invalid values that the live renderer's safeParse then drops.
 *
 * Ops come in 4 kinds:
 *  - set_prop    — edit a prop on an existing node
 *  - add_node    — insert a new component into a parent that accepts children
 *  - remove_node — delete a node (root cannot be removed)
 *  - move_node   — shift a node up/down among its siblings
 */

import { registry, type RegistryName } from "@dynamic-landing/components";
import {
  BLOCK_ALIGN_VALUES,
  BLOCK_WIDTH_VALUES,
  COL_SPAN_VALUES,
  getLayoutRecipe,
  isLayoutParentName,
  ROW_SPAN_VALUES,
  STACK_RECIPE_ID,
} from "@dynamic-landing/layout";

import { describeSchema, type FieldDescriptor, type SchemaLike } from "./schemaIntrospect";
import type {
  AddNodeOp,
  ApplyRecipeOp,
  ComponentNode,
  ComponentTree,
  MoveNodeOp,
  ProposalOp,
  RemoveNodeOp,
  SetPropOp,
  UnwrapNodeOp,
  WrapNodesOp,
} from "./types";
import { opKind } from "./types";

type RegistryEntry = (typeof registry)[RegistryName];

export type OpValidation = { ok: true } | { ok: false; reason: string };

function findNode(node: ComponentNode, id: string): ComponentNode | null {
  if (node.node_id === id) return node;
  for (const c of node.children ?? []) {
    const hit = findNode(c, id);
    if (hit) return hit;
  }
  return null;
}

/** Find the parent of `id`, or null if it's the root / not present. */
function findParentNode(node: ComponentNode, id: string): ComponentNode | null {
  for (const c of node.children ?? []) {
    if (c.node_id === id) return node;
    const hit = findParentNode(c, id);
    if (hit) return hit;
  }
  return null;
}

/** colSpan/rowSpan are generic PLACEMENT props (not in any component schema):
 *  valid only on a direct child of a layout parent, with a curated span value. */
function validatePlacement(tree: ComponentTree, op: SetPropOp): OpValidation {
  const parent = findParentNode(tree.root, op.node_id);
  if (!parent || !isLayoutParentName(parent.component.name)) {
    return {
      ok: false,
      reason: `"${op.prop}" only applies to a block placed directly inside a Grid/Columns`,
    };
  }
  // Clearing the span (undefined / "") is always allowed.
  if (op.value === undefined || op.value === "") return { ok: true };
  const allowed: readonly string[] = op.prop === "colSpan" ? COL_SPAN_VALUES : ROW_SPAN_VALUES;
  const v = typeof op.value === "number" ? String(op.value) : op.value;
  if (typeof v !== "string" || !allowed.includes(v)) {
    return { ok: false, reason: `"${op.prop}" must be one of: ${allowed.join(" | ")}` };
  }
  return { ok: true };
}

function fieldFor(componentName: string, propName: string): FieldDescriptor | null {
  const reg = registry as Record<string, RegistryEntry | undefined>;
  const entry = reg[componentName];
  if (!entry) return null;
  const fields = describeSchema(entry.schema as unknown as SchemaLike);
  return fields.find((f) => f.name === propName) ?? null;
}

function validateValueAgainstField(
  field: FieldDescriptor,
  v: unknown,
  propName: string,
): OpValidation {
  switch (field.kind.kind) {
    case "string":
      if (typeof v !== "string") {
        return { ok: false, reason: `prop "${propName}" expects a string` };
      }
      return { ok: true };
    case "number":
      if (typeof v !== "number") {
        return { ok: false, reason: `prop "${propName}" expects a number` };
      }
      return { ok: true };
    case "boolean":
      if (typeof v !== "boolean") {
        return { ok: false, reason: `prop "${propName}" expects a boolean` };
      }
      return { ok: true };
    case "enum": {
      const allowed = field.kind.options;
      if (typeof v !== "string" || !allowed.includes(v)) {
        return {
          ok: false,
          reason: `prop "${propName}" must be one of: ${allowed.join(" | ")}`,
        };
      }
      return { ok: true };
    }
    case "json":
      return { ok: true };
  }
}

/** blockWidth/blockAlign are generic SIZING props (not in any component schema):
 *  valid on any node, with a curated value. Clearing (undefined/"") is allowed. */
function validateBlockSize(op: SetPropOp): OpValidation {
  if (op.value === undefined || op.value === "") return { ok: true };
  const allowed: readonly string[] = op.prop === "blockWidth" ? BLOCK_WIDTH_VALUES : BLOCK_ALIGN_VALUES;
  if (typeof op.value !== "string" || !allowed.includes(op.value)) {
    return { ok: false, reason: `"${op.prop}" must be one of: ${allowed.join(" | ")}` };
  }
  return { ok: true };
}

function validateSetProp(tree: ComponentTree, op: SetPropOp): OpValidation {
  const node = findNode(tree.root, op.node_id);
  if (!node) {
    return { ok: false, reason: `node "${op.node_id}" not found in tree` };
  }
  // Grid placement props are generic (read by TreeRenderer, not in any schema).
  if (op.prop === "colSpan" || op.prop === "rowSpan") {
    return validatePlacement(tree, op);
  }
  // Block sizing props are generic too (apply to any node).
  if (op.prop === "blockWidth" || op.prop === "blockAlign") {
    return validateBlockSize(op);
  }
  const field = fieldFor(node.component.name, op.prop);
  if (!field) {
    return { ok: false, reason: `${node.component.name} has no prop "${op.prop}"` };
  }
  return validateValueAgainstField(field, op.value, op.prop);
}

function validateAddNode(tree: ComponentTree, op: AddNodeOp): OpValidation {
  const parent = findNode(tree.root, op.parent_node_id);
  if (!parent) {
    return { ok: false, reason: `parent "${op.parent_node_id}" not found in tree` };
  }
  const reg = registry as Record<string, RegistryEntry | undefined>;
  const parentEntry = reg[parent.component.name];
  if (!parentEntry?.acceptsChildren) {
    return { ok: false, reason: `${parent.component.name} cannot contain children` };
  }
  const childEntry = reg[op.component];
  if (!childEntry) {
    return { ok: false, reason: `unknown component "${op.component}"` };
  }
  // Validate provided props against the new child's schema.
  if (op.props) {
    const fields = describeSchema(childEntry.schema as unknown as SchemaLike);
    for (const [k, v] of Object.entries(op.props)) {
      const field = fields.find((f) => f.name === k);
      if (!field) {
        return { ok: false, reason: `${op.component} has no prop "${k}"` };
      }
      const r = validateValueAgainstField(field, v, k);
      if (!r.ok) return r;
    }
  }
  // Required props (zero-arg `.parse({})` would fail) must be present.
  const childSchema = childEntry.schema as unknown as SchemaLike;
  const dryRun = childSchema.safeParse(op.props ?? {});
  if (!dryRun.success) {
    const missing = dryRun.error.issues
      .filter((i) => i.code === "invalid_type" && i.received === "undefined")
      .map((i) => i.path.join("."))
      .filter(Boolean);
    if (missing.length > 0) {
      return {
        ok: false,
        reason: `${op.component} missing required prop(s): ${missing.join(", ")}`,
      };
    }
  }
  return { ok: true };
}

function validateRemoveNode(tree: ComponentTree, op: RemoveNodeOp): OpValidation {
  if (tree.root.node_id === op.node_id) {
    return { ok: false, reason: "cannot remove the root node" };
  }
  const node = findNode(tree.root, op.node_id);
  if (!node) {
    return { ok: false, reason: `node "${op.node_id}" not found in tree` };
  }
  return { ok: true };
}

function validateMoveNode(tree: ComponentTree, op: MoveNodeOp): OpValidation {
  if (tree.root.node_id === op.node_id) {
    return { ok: false, reason: "cannot move the root node" };
  }
  const node = findNode(tree.root, op.node_id);
  if (!node) {
    return { ok: false, reason: `node "${op.node_id}" not found in tree` };
  }
  // Re-parent variant: move into a different parent.
  if (op.to_parent_id !== undefined) {
    if (op.to_parent_id === op.node_id) {
      return { ok: false, reason: "cannot move a node into itself" };
    }
    const target = findNode(tree.root, op.to_parent_id);
    if (!target) {
      return { ok: false, reason: `parent "${op.to_parent_id}" not found in tree` };
    }
    const reg = registry as Record<string, RegistryEntry | undefined>;
    if (!reg[target.component.name]?.acceptsChildren) {
      return { ok: false, reason: `${target.component.name} cannot contain children` };
    }
    if (findNode(node, op.to_parent_id)) {
      return { ok: false, reason: "cannot move a node inside its own descendant" };
    }
    return { ok: true };
  }
  // Sibling-shift variant (legacy).
  if (op.delta !== -1 && op.delta !== 1) {
    return { ok: false, reason: "move needs delta (-1/+1) or to_parent_id" };
  }
  return { ok: true };
}

function validateWrapNodes(tree: ComponentTree, op: WrapNodesOp): OpValidation {
  const ids = op.node_ids ?? [];
  if (ids.length === 0) {
    return { ok: false, reason: "wrap_nodes needs at least one node_id" };
  }
  const reg = registry as Record<string, RegistryEntry | undefined>;
  const containerEntry = reg[op.component];
  if (!containerEntry) {
    return { ok: false, reason: `unknown component "${op.component}"` };
  }
  if (!containerEntry.acceptsChildren) {
    return { ok: false, reason: `${op.component} cannot contain children` };
  }
  // Every id must exist, not be the root, and share ONE parent.
  const parents = new Set<string | null>();
  for (const id of ids) {
    if (id === tree.root.node_id) return { ok: false, reason: "cannot wrap the root node" };
    if (!findNode(tree.root, id)) return { ok: false, reason: `node "${id}" not found in tree` };
    const parent = findParentNode(tree.root, id);
    parents.add(parent ? parent.node_id : null);
  }
  if (parents.size !== 1) {
    return { ok: false, reason: "wrap_nodes requires all blocks to be siblings (one parent)" };
  }
  return { ok: true };
}

function validateUnwrapNode(tree: ComponentTree, op: UnwrapNodeOp): OpValidation {
  if (tree.root.node_id === op.node_id) {
    return { ok: false, reason: "cannot unwrap the root node" };
  }
  const node = findNode(tree.root, op.node_id);
  if (!node) {
    return { ok: false, reason: `node "${op.node_id}" not found in tree` };
  }
  const reg = registry as Record<string, RegistryEntry | undefined>;
  if (!reg[node.component.name]?.acceptsChildren) {
    return { ok: false, reason: `${node.component.name} is not a container (nothing to ungroup)` };
  }
  return { ok: true };
}

function validateApplyRecipe(tree: ComponentTree, op: ApplyRecipeOp): OpValidation {
  if (op.recipe_id === STACK_RECIPE_ID || getLayoutRecipe(op.recipe_id)) {
    // A recipe on an empty page is a structural no-op, not an error — but a
    // valid recipe id on a page with blocks is the actionable case the UI shows.
    if ((tree.root.children?.length ?? 0) === 0) {
      return { ok: false, reason: "page has no blocks to lay out yet" };
    }
    return { ok: true };
  }
  return {
    ok: false,
    reason: `unknown layout recipe "${op.recipe_id}" (use bento | editorial | feature-grid | brutalist | stack)`,
  };
}

export function validateOp(tree: ComponentTree, op: ProposalOp): OpValidation {
  switch (opKind(op)) {
    case "set_prop":
      return validateSetProp(tree, op as SetPropOp);
    case "add_node":
      return validateAddNode(tree, op as AddNodeOp);
    case "remove_node":
      return validateRemoveNode(tree, op as RemoveNodeOp);
    case "move_node":
      return validateMoveNode(tree, op as MoveNodeOp);
    case "apply_recipe":
      return validateApplyRecipe(tree, op as ApplyRecipeOp);
    case "wrap_nodes":
      return validateWrapNodes(tree, op as WrapNodesOp);
    case "unwrap_node":
      return validateUnwrapNode(tree, op as UnwrapNodeOp);
  }
}
